import React, { useState, useEffect, memo, useCallback } from 'react';
import { useUser } from '../contexts/UserContext';
import { supabase } from '../supabase/config';
import { logger } from '../utils/logger';

const GoalSetting = memo(({ child, onGoalCreated }) => {
  const { user } = useUser();
  const [goals, setGoals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    goal_type: 'daily',
    title: '',
    description: '',
    target_value: 1,
    unit: 'lessons',
    subject: '',
    end_date: ''
  });

  useEffect(() => {
    if (child?.id) {
      loadGoals();
    }
  }, [child?.id]);

  const loadGoals = async () => {
    if (!child?.id || !user?.id) return;

    setIsLoading(true);
    try {
      const { data, error: fetchError } = await supabase
        .from('learning_goals')
        .select('*')
        .eq('child_id', child.id)
        .eq('parent_id', user.id)
        .in('status', ['active', 'paused'])
        .order('created_at', { ascending: false });

      if (fetchError) {
        // Table might not exist yet - that's okay
        if (fetchError.code === '42P01') {
          logger.warn('Learning goals table does not exist yet');
          setGoals([]);
        } else {
          throw fetchError;
        }
      } else {
        setGoals(data || []);
      }
    } catch (error) {
      logger.error('Error loading goals:', error);
      setGoals([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!child?.id || !user?.id) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const goalData = {
        child_id: child.id,
        parent_id: user.id,
        goal_type: formData.goal_type,
        title: formData.title,
        description: formData.description || null,
        target_value: parseInt(formData.target_value),
        unit: formData.unit,
        subject: formData.subject || null,
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null,
        status: 'active'
      };

      const { data, error: insertError } = await supabase
        .from('learning_goals')
        .insert(goalData)
        .select()
        .single();

      if (insertError) {
        throw insertError;
      }

      logger.info('Goal created:', data);
      setGoals([data, ...goals]);
      setShowForm(false);
      setFormData({
        goal_type: 'daily',
        title: '',
        description: '',
        target_value: 1,
        unit: 'lessons',
        subject: '',
        end_date: ''
      });

      if (onGoalCreated) {
        onGoalCreated(data);
      }
    } catch (error) {
      logger.error('Error creating goal:', error);
      setError(error.message || 'Failed to create goal');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (goalId) => {
    if (!confirm('Are you sure you want to delete this goal?')) return;

    try {
      const { error: deleteError } = await supabase
        .from('learning_goals')
        .update({ status: 'cancelled' })
        .eq('id', goalId);

      if (deleteError) throw deleteError;

      setGoals(goals.filter(g => g.id !== goalId));
    } catch (error) {
      logger.error('Error deleting goal:', error);
      setError(error.message || 'Failed to delete goal');
    }
  };

  const getProgressPercentage = (goal) => {
    return Math.min((goal.current_value / goal.target_value) * 100, 100);
  };

  const getSubjectOptions = () => {
    return [
      { value: '', label: 'All Subjects' },
      { value: 'phonics', label: 'Phonics' },
      { value: 'math', label: 'Math' },
      { value: 'reading', label: 'Reading' },
      { value: 'spelling', label: 'Spelling' },
      { value: 'science', label: 'Science' },
      { value: 'social', label: 'Social Studies' },
      { value: 'art', label: 'Art' },
      { value: 'geography', label: 'Geography' },
      { value: 'history', label: 'History' }
    ];
  };

  if (!child) {
    return (
      <div className="text-center py-8 text-gray-500">
        Select a child to set learning goals
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <span>🎯</span>
          <span>Learning Goals</span>
        </h3>
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
        >
          {showForm ? '✕ Cancel' : '+ New Goal'}
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      {/* Goal Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-gray-50 rounded-lg p-4 mb-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Goal Type
            </label>
            <select
              value={formData.goal_type}
              onChange={(e) => setFormData({ ...formData, goal_type: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              required
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="subject">Subject-Specific</option>
              <option value="custom">Custom</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              placeholder="e.g., Complete 5 lessons this week"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              rows="2"
              placeholder="Optional description..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Target Value *
              </label>
              <input
                type="number"
                value={formData.target_value}
                onChange={(e) => setFormData({ ...formData, target_value: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                min="1"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Unit
              </label>
              <select
                value={formData.unit}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="lessons">Lessons</option>
                <option value="minutes">Minutes</option>
                <option value="questions">Questions</option>
                <option value="points">Points</option>
                <option value="subjects">Subjects</option>
              </select>
            </div>
          </div>

          {(formData.goal_type === 'subject' || formData.goal_type === 'custom') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subject (Optional)
              </label>
              <select
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                {getSubjectOptions().map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date (Optional)
            </label>
            <input
              type="date"
              value={formData.end_date}
              onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50"
          >
            {isSubmitting ? 'Creating...' : 'Create Goal'}
          </button>
        </form>
      )}

      {/* Goals List */}
      {isLoading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-2"></div>
          <p className="text-gray-600 text-sm">Loading goals...</p>
        </div>
      ) : goals.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <div className="text-4xl mb-2">🎯</div>
          <p className="text-gray-600">No active goals</p>
          <p className="text-sm text-gray-500 mt-1">Create a goal to help track progress</p>
        </div>
      ) : (
        <div className="space-y-4">
          {goals.map(goal => {
            const progress = getProgressPercentage(goal);
            const isCompleted = goal.status === 'completed' || progress >= 100;

            return (
              <div
                key={goal.id}
                className={`border rounded-lg p-4 ${
                  isCompleted ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-gray-900">{goal.title}</span>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        goal.goal_type === 'daily' ? 'bg-blue-100 text-blue-800' :
                        goal.goal_type === 'weekly' ? 'bg-purple-100 text-purple-800' :
                        goal.goal_type === 'subject' ? 'bg-green-100 text-green-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {goal.goal_type}
                      </span>
                      {isCompleted && (
                        <span className="text-xs px-2 py-0.5 rounded bg-green-200 text-green-800">
                          ✓ Completed
                        </span>
                      )}
                    </div>
                    {goal.description && (
                      <p className="text-sm text-gray-600 mb-2">{goal.description}</p>
                    )}
                    <div className="text-sm text-gray-600">
                      <span className="font-semibold">{goal.current_value}</span> / {goal.target_value} {goal.unit}
                      {goal.subject && (
                        <span className="ml-2 text-purple-600">• {goal.subject}</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(goal.id)}
                    className="text-red-600 hover:text-red-800 text-sm"
                    title="Delete goal"
                  >
                    ✕
                  </button>
                </div>

                {/* Progress Bar */}
                <div className="w-full bg-gray-200 rounded-full h-2 mt-3">
                  <div
                    className={`h-2 rounded-full transition-all ${
                      isCompleted ? 'bg-green-500' : 'bg-purple-500'
                    }`}
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <div className="text-xs text-gray-500 mt-1 text-right">
                  {Math.round(progress)}% complete
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
});

GoalSetting.displayName = 'GoalSetting';

export default GoalSetting;

